import type { KpiAction } from "@excellent/core";
import { WorkGroup } from "../work-group";
import { WorkList, type WorkRowModel } from "../work-row";

/**
 * KpiActionList — the "Move these numbers" region: the how-do-I-improve-this
 * layer of a department dashboard. Each row anchors to a hero/driver KPI that is
 * off target or has no data yet, and offers the registry job that moves it (DASHBOARD-REDESIGN-PRD; Department-JTBD registry). Quiet over
 * loud: renders nothing when every metric is on track. Pure presentation — the
 * server resolves which actions surface (excellent/src/projects/dashboards.ts →
 * buildKpiActions → derive.ts kpiActionForJob).
 *
 * ── On WorkRow (2026-07-23) ──────────────────────────────────────────────────
 * The hand-rolled Card + header + `<ul>` of flex rows became the canonical
 * container + row: `WorkGroup` (the one titled card for a work list) around a
 * `WorkList` (`docs/design/WORK-ROW-SYSTEM.md`). The KPI each action anchors to
 * is the row's single tag; an off-target metric is the row's toned notice; and
 * the CTA collapses into the row's own href, so the whole row is the click
 * target the standalone button used to be.
 */

/** Reason → the short note it surfaced under: the metric missed target, or has
 *  no data to show yet. `off-track` is the row's warning notice; `thin` is
 *  neutral (nobody's needed yet), so it carries no mark and rides the subtitle. */
const REASON_NOTE: Record<KpiAction["reason"], string> = {
  "off-track": "Off target",
  thin: "No data yet",
};

/**
 * KpiAction → WorkRowModel — one mapping for the whole list. The imperative job
 * is the title; the KPI it moves is the row's one tag (dimension "KPI"); an
 * off-target metric is the warning notice; the resolved CTA href is the row's
 * own doorway.
 */
function kpiActionToRow(a: KpiAction, i: number): WorkRowModel {
  return {
    id: `${a.kpiId}-${a.label}-${i}`,
    title: a.label,
    // The live trigger / KPI gap; falls back to the reason note so a thin row
    // still says "No data yet" when it carries no detail of its own.
    subtitle: a.detail ?? a.goal ?? REASON_NOTE[a.reason],
    pills: [{ label: a.kpiLabel, hint: "KPI" }],
    attention:
      a.reason === "off-track"
        ? { label: REASON_NOTE["off-track"], level: "warning" }
        : undefined,
    href: a.href,
  };
}

export function KpiActionList({
  actions,
  title = "Move these numbers",
}: {
  actions: KpiAction[] | undefined;
  title?: string;
}) {
  if (!actions || actions.length === 0) return null;

  // Not collapsible: this region is a static header + rows, never a disclosure.
  return (
    <WorkGroup title={title} count={actions.length} collapsible={false}>
      <WorkList rows={actions.map(kpiActionToRow)} gutter />
    </WorkGroup>
  );
}
